import type { PassagePage, ReadingQuestion } from "@examing/types";

export interface ResolvePassagePagesOptions {
  pages?: PassagePage[];
  subQuestions?: ReadingQuestion[];
  passageText?: string;
  passageTitle?: string;
  defaultTitle?: (index: number) => string;
  generateId?: (index: number) => string;
}

/**
 * Resolves structured pages from either the explicit `pages` property,
 * or by reconstructing them from `subQuestions` with `pageIndex` / `pageId`,
 * or by returning a single page containing the root passage.
 */
export function resolvePassagePages({
  pages,
  subQuestions = [],
  passageText = "",
  passageTitle = "",
  defaultTitle = (idx: number) => `Passage ${idx + 1}`,
  generateId = (idx: number) => `p-${idx + 1}`,
}: ResolvePassagePagesOptions): PassagePage[] {
  // Case A: Structured pages already provided
  if (pages && pages.length > 0) {
    return pages.map((p, idx) => {
      let subs = p.subQuestions;
      if (!subs || subs.length === 0) {
        subs = subQuestions.filter(
          (q) =>
            q.pageIndex !== undefined
              ? q.pageIndex === idx
              : q.pageId === p.id,
        );
      }
      return {
        ...p,
        passageTitle:
          p.passageTitle ||
          (idx === 0 && passageTitle ? passageTitle : defaultTitle(idx)),
        passageText: p.passageText || (idx === 0 ? passageText : ""),
        subQuestions: subs || [],
      };
    });
  }

  // Case B: Subquestions contain page split indicators (pageIndex > 0 or pageId present)
  const hasMultiplePages = subQuestions.some(
    (q) => (q.pageIndex !== undefined && q.pageIndex > 0) || Boolean(q.pageId),
  );

  if (hasMultiplePages) {
    const map = new Map<number, ReadingQuestion[]>();
    for (const q of subQuestions) {
      const pIdx = q.pageIndex ?? 0;
      if (!map.has(pIdx)) map.set(pIdx, []);
      map.get(pIdx)!.push(q);
    }
    const maxIdx = Math.max(...Array.from(map.keys()), 0);
    const result: PassagePage[] = [];
    for (let i = 0; i <= maxIdx; i++) {
      result.push({
        id: generateId(i),
        passageTitle:
          i === 0 && passageTitle ? passageTitle : defaultTitle(i),
        passageText: i === 0 ? passageText : "",
        subQuestions: map.get(i) || [],
      });
    }
    return result;
  }

  // Case C: Single unified page
  return [
    {
      id: generateId(0),
      passageTitle: passageTitle || "",
      passageText: passageText || "",
      subQuestions: subQuestions || [],
    },
  ];
}

/**
 * Calculates word count and estimated reading time from HTML or plain text.
 */
export function estimateReadingStats(htmlOrText: string): {
  wordCount: number;
  readTimeMin: number;
} {
  const textOnly = (htmlOrText || "").replace(/<[^>]*>/g, " ").trim();
  if (!textOnly) {
    return { wordCount: 0, readTimeMin: 1 };
  }
  const wordCount = textOnly.split(/\s+/).filter(Boolean).length;
  const readTimeMin = Math.max(1, Math.ceil(wordCount / 200));
  return { wordCount, readTimeMin };
}
